import { apiClient, ApiResponse } from './apiClient';
import {
  Product,
  PaginatedProductsResponse,
  CreateProductData,
  UpdateProductData,
  ConvertArchiveToProductData,
} from '../types/products.types';

/**
 * Products Service
 * Handles fashion product catalog management
 */

export interface ProductParams {
  page?: number;
  limit?: number;
  collectionId?: string;
  userId?: string;
  status?: 'draft' | 'published';
  sortBy?: string;
  order?: 'asc' | 'desc';
}

export const productsService = {
  /**
   * Get products (public endpoint)
   * GET /products
   */
  getProducts: async (params?: ProductParams): Promise<ApiResponse<PaginatedProductsResponse>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.collectionId) queryParams.append('collectionId', params.collectionId);
    if (params?.userId) queryParams.append('userId', params.userId);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.order) queryParams.append('order', params.order);

    const url = `/products${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return await apiClient.get<PaginatedProductsResponse>(url);
  },

  /**
   * Get single product by ID
   * GET /products/:id
   */
  getById: async (id: string): Promise<ApiResponse<{ product: Product }>> => {
    return await apiClient.get<{ product: Product }>(`/products/${id}`);
  },

  /**
   * Get authenticated user's products
   * GET /products/my/all
   */
  getMyProducts: async (
    params?: Omit<ProductParams, 'userId'>
  ): Promise<ApiResponse<PaginatedProductsResponse>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.order) queryParams.append('order', params.order);

    const url = `/products/my/all${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return await apiClient.get<PaginatedProductsResponse>(url);
  },

  /**
   * Create product
   * POST /products (multipart/form-data)
   */
  create: async (data: CreateProductData): Promise<ApiResponse<{ product: Product }>> => {
    const formData = new FormData();
    
    // Add required fields
    formData.append('collectionId', data.collectionId);
    formData.append('name', data.name);
    
    // Add optional text fields
    if (data.description) formData.append('description', data.description);
    if (data.price !== undefined) formData.append('price', data.price.toString());
    if (data.currency) formData.append('currency', data.currency);
    if (data.careInstructions) formData.append('careInstructions', data.careInstructions);
    if (data.stockQuantity !== undefined) formData.append('stockQuantity', data.stockQuantity.toString());
    if (data.sku) formData.append('sku', data.sku);
    if (data.status) formData.append('status', data.status);
    if (data.acceptOrders !== undefined) formData.append('acceptOrders', data.acceptOrders.toString());
    
    // Add array fields as JSON
    if (data.sizes && data.sizes.length > 0) {
      formData.append('sizes', JSON.stringify(data.sizes));
    }
    if (data.colors && data.colors.length > 0) {
      formData.append('colors', JSON.stringify(data.colors));
    }
    if (data.materials && data.materials.length > 0) {
      formData.append('materials', JSON.stringify(data.materials));
    }
    
    // Add images (max 10)
    if (data.images && data.images.length > 0) {
      data.images.forEach(image => {
        formData.append('images', image);
      });
    }
    
    // Add media library IDs
    if (data.mediaLibraryIds && data.mediaLibraryIds.length > 0) {
      formData.append('mediaLibraryIds', JSON.stringify(data.mediaLibraryIds));
    }

    return await apiClient.uploadFile<{ product: Product }>('/products', formData);
  },

  /**
   * Update product
   * PATCH /products/:id (multipart/form-data if images included)
   */
  update: async (
    id: string,
    data: UpdateProductData
  ): Promise<ApiResponse<{ product: Product }>> => {
    // If images are included, use FormData
    if (data.images && data.images.length > 0) {
      const formData = new FormData();
      
      // Add text fields
      if (data.name) formData.append('name', data.name);
      if (data.description !== undefined) formData.append('description', data.description);
      if (data.price !== undefined) formData.append('price', data.price.toString());
      if (data.currency) formData.append('currency', data.currency);
      if (data.careInstructions !== undefined) formData.append('careInstructions', data.careInstructions);
      if (data.stockQuantity !== undefined) formData.append('stockQuantity', data.stockQuantity.toString());
      if (data.sku !== undefined) formData.append('sku', data.sku);
      if (data.status) formData.append('status', data.status);
      if (data.acceptOrders !== undefined) formData.append('acceptOrders', data.acceptOrders.toString());
      
      // Add array fields as JSON
      if (data.sizes) formData.append('sizes', JSON.stringify(data.sizes));
      if (data.colors) formData.append('colors', JSON.stringify(data.colors));
      if (data.materials) formData.append('materials', JSON.stringify(data.materials));
      
      // Add existing image IDs to keep
      if (data.existingImageIds) {
        formData.append('existingImageIds', JSON.stringify(data.existingImageIds));
      }
      
      // Add media library IDs
      if (data.mediaLibraryIds && data.mediaLibraryIds.length > 0) {
        formData.append('mediaLibraryIds', JSON.stringify(data.mediaLibraryIds));
      }
      
      // Add new images
      data.images.forEach(image => {
        formData.append('images', image);
      });

      return await apiClient.uploadFile<{ product: Product }>(`/products/${id}`, formData, 'PATCH');
    }
    
    // Otherwise use regular JSON patch
    return await apiClient.patch<{ product: Product }>(`/products/${id}`, data);
  },

  /**
   * Delete product
   * DELETE /products/:id
   */
  remove: async (id: string): Promise<ApiResponse<{ success: boolean }>> => {
    return await apiClient.delete<{ success: boolean }>(`/products/${id}`);
  },

  /**
   * Add images to product
   * POST /products/:id/images (multipart/form-data)
   */
  addImages: async (
    productId: string,
    images: File[]
  ): Promise<ApiResponse<{ images: any[] }>> => {
    const formData = new FormData();
    images.forEach(image => {
      formData.append('images', image);
    });

    return await apiClient.uploadFile<{ images: any[] }>(`/products/${productId}/images`, formData);
  },

  /**
   * Delete product image
   * DELETE /products/:productId/images/:imageId
   */
  removeImage: async (
    productId: string,
    imageId: string
  ): Promise<ApiResponse<{ success: boolean }>> => {
    return await apiClient.delete<{ success: boolean }>(
      `/products/${productId}/images/${imageId}`
    );
  },

  /**
   * Convert archive post to product
   * POST /products/convert-from-archive
   */
  convertFromArchive: async (
    data: ConvertArchiveToProductData
  ): Promise<ApiResponse<{ product: Product }>> => {
    return await apiClient.post<{ product: Product }>('/products/convert-from-archive', data);
  },
};
